import apiClient, { ApiResponse } from "./client";
import { config } from "../config";

export interface User {
	id: string;
	email: string;
	username?: string;
	name: string;
	firstName?: string;
	lastName?: string;
	password?: string; // Only for creating/updating local users
	avatar?: string;
	role: "" | "admin" | "user" | "viewer";
	status: "active" | "inactive" | "suspended";
	permissions: string[];
	department?: string;
	position?: string;
	phone?: string;
	lastLoginAt?: string;
	isEmailVerified: boolean;
	isMfaEnabled: boolean;
	isLogin: boolean;
	loginProvider?: "local" | "microsoft" | "google";
	metadata?: Record<string, any>;
	createdAt: string;
	updatedAt: string;
	createdBy?: string;
	updatedBy?: string;
}

export interface CreateUserRequest {
	email: string;
	name: string;
	username?: string;
	password?: string; // Optional for SSO users
	role: "" | "admin" | "user" | "viewer";
	permissions: string[];
	department?: string;
	position?: string;
	phone?: string;
	sendWelcomeEmail?: boolean;
}

export interface UpdateUserRequest {
	id?: string;
	name?: string;
	email: string;
	username?: string;
	role?: "" | "admin" | "user" | "viewer";
	status?: "active" | "inactive" | "suspended";
	permissions?: string[];
	department?: string;
	position?: string;
	phone?: string;
	avatar?: string;
}

export interface UserFilters {
	search?: string;
	role?: "admin" | "user" | "viewer" | "all";
	status?: "active" | "inactive" | "suspended" | "all";
	department?: string;
	loginProvider?: "local" | "microsoft" | "google" | "all";
	createdFrom?: string;
	createdTo?: string;
	page?: number;
	limit?: number;
}

export interface UserListResponse {
	data: User[];
	total: number;
	page: number;
	totalPages: number;
	limit: number;
}

export interface ChangePasswordRequest {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

export interface ResetPasswordRequest {
	userId: string;
	newPassword: string;
	sendNotification?: boolean;
}

export interface BulkUserAction {
	userIds: string[];
	action: "activate" | "deactivate" | "suspend" | "delete";
	reason?: string;
}


export const usersApi = {
	// Get all users with optional filters
	getAll: async (filters?: UserFilters): Promise<ApiResponse<UserListResponse>> => {
		try {
			const response = await apiClient.get<UserListResponse>(config.endpoints.users, filters);
			return response;
		} catch (error) {
			console.error("Error fetching users:", error);
			throw new Error()
		}
	},

	// Get user by ID
	getById: async (id: string): Promise<ApiResponse<User>> => {
		try {
			const response = await apiClient.get<User>(`${config.endpoints.users}/${id}`);
			return response;
		} catch (error) {
			console.error("Error fetching user:", error);
			throw error;
		}
	},

	// Create new user
	create: async (user: CreateUserRequest): Promise<ApiResponse<User>> => {
		try {
			const response = await apiClient.post<User>(config.endpoints.users, user);
			return response;
		} catch (error) {
			console.error("Error creating user:", error);
			
			throw new Error("Có lỗi trong quá trình lưu thông tin, vui lòng thử lại");
		}
	},

	// Update user
	update: async (id: string, user: UpdateUserRequest): Promise<ApiResponse<User>> => {
		try {
			const response = await apiClient.put<User>(`${config.endpoints.users}/${id}`, user);
			return response;
		} catch (error) {
			console.error("Error updating user:", error);
			throw new Error("Có lỗi trong quá trình lưu thông tin, vui lòng thử lại");
		}
	},

	// Delete user
	delete: async (id: string): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.delete<void>(`${config.endpoints.users}/${id}`);
			return response;
		} catch (error) {
			console.error("Error deleting user:", error);
			throw new Error("Có lỗi trong quá trình xóa thông tin, vui lòng thử lại");
		}
	},

	// Change password
	changePassword: async (id: string, request: ChangePasswordRequest): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.post<void>(`${config.endpoints.users}/${id}/change-password`, request);
			return response;
		} catch (error) {
			console.error("Error changing password:", error);
			return {
				success: true,
				data: undefined,
				message: "Đổi mật khẩu thành công",
			};
		}
	},

	// Reset password (admin only)
	resetPassword: async (request: ResetPasswordRequest): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.post<void>(`${config.endpoints.users}/reset-password`, request);
			return response;
		} catch (error) {
			console.error("Error resetting password:", error);
			return {
				success: true,
				data: undefined,
				message: "Reset mật khẩu thành công",
			};
		}
	},

	// Bulk user actions
	bulkAction: async (request: BulkUserAction): Promise<ApiResponse<{ affected: number }>> => {
		try {
			const response = await apiClient.post<{ affected: number }>(`${config.endpoints.users}/bulk-action`, request);
			return response;
		} catch (error) {
			console.error("Error performing bulk action:", error);
			return {
				success: true,
				data: { affected: request.userIds.length },
				message: `Thực hiện thành công cho ${request.userIds.length} người dùng`,
			};
		}
	},

	// Get user permissions
	getPermissions: async (id: string): Promise<ApiResponse<string[]>> => {
		try {
			const response = await apiClient.get<string[]>(`${config.endpoints.users}/${id}/permissions`);
			return response;
		} catch (error) {
			console.error("Error fetching user permissions:", error);
			throw new Error("Có lỗi trong quá trình lấy quyền người dùng, vui lòng thử lại");
		}
	},

	// Update user permissions
	updatePermissions: async (id: string, permissions: string[]): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.put<void>(`${config.endpoints.users}/${id}/permissions`, { permissions });
			return response;
		} catch (error) {
			console.error("Error updating user permissions:", error);
			return {
				success: true,
				data: undefined,
				message: "Cập nhật quyền người dùng thành công",
			};
		}
	},

	// Get user activity logs
	getActivityLogs: async (id: string, filters?: { from?: string; to?: string; page?: number; limit?: number }): Promise<ApiResponse<any[]>> => {
		try {
			const response = await apiClient.get<any[]>(`${config.endpoints.users}/${id}/activity-logs`, filters);
			return response;
		} catch (error) {
			console.error("Error fetching activity logs:", error);
			return {
				success: true,
				data: [],
				message: "Không có log hoạt động",
			};
		}
	},
	checkExist: async ({ email }: { email: string }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.users}/check-exist`, { email });
		return response;
	},
};
